import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@patient-watch/ui/card";
import type { StatefulDiaryT } from "@patient-watch/types";
import TrackerChart, { shouldShowTrackerChart } from "./TrackerChart";
import { cn } from "@patient-watch/ui";
import { formatTrackerDisplayTitle } from "./utils/formatTrackerDisplayTitle";
import { DEFAULT_LOCALE, type SupportedLocale } from "@patient-watch/i18n";
import NoDataPlaceholder from "./NoDataPlaceholder";

interface Props {
    diary: StatefulDiaryT;
    printMode?: boolean;
    locale?: SupportedLocale;
}

const DiaryCharts = ({ diary, printMode = false, locale = DEFAULT_LOCALE }: Props) => {
    const hasTrackers = diary.trackers_view?.length > 0;
    const hasEntries = diary.trackers_view?.some((tracker) => tracker.entries_view?.length > 0);

    if (!hasTrackers || !hasEntries) {
        return <NoDataPlaceholder locale={locale} />;
    }

    // Filter trackers to only those with chartable numeric data and show_chart enabled
    const chartableTrackers = diary.trackers_view.filter((tracker) => shouldShowTrackerChart(tracker));

    if (chartableTrackers.length === 0) {
        return <NoDataPlaceholder locale={locale} />;
    }

    return (
        <section className="space-y-4">
            {chartableTrackers.map((tracker) => {
                if (!tracker) return null;

                const formattedTitle = formatTrackerDisplayTitle(
                    tracker.form?.form_name,
                    tracker.side,
                    tracker.form?.form_name ?? "",
                    locale
                );
                const formattedDescription =
                    tracker.form?.form_description
                        ?.split(" ")
                        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                        .join(" ") ?? formattedTitle;

                return (
                    <Card
                        key={tracker.tracker_id}
                        className={cn("p-4", printMode && "break-inside-avoid page-break-inside-avoid")}
                    >
                        <CardHeader>
                            <CardTitle className="font-semibold text-xl">{formattedTitle}</CardTitle>
                            <CardDescription>{formattedDescription}</CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div
                                className={cn(
                                    "overflow-visible",
                                    printMode &&
                                        "overflow-hidden w-[700px] max-w-[700px] min-w-[700px] min-h-[400px] scale-100"
                                )}
                            >
                                <TrackerChart tracker={tracker} printMode={printMode} locale={locale} />
                            </div>
                        </CardContent>
                    </Card>
                );
            })}
        </section>
    );
};

export default DiaryCharts;
export { DiaryCharts };
